import { useState } from 'react'
import { Pressable, StyleSheet, Text, TextInput, View } from 'react-native'
import { colors, fontFamily } from '../tokens'
import { trpc } from '../trpc'
import { ErrorNotice } from './ErrorNotice'

type Outcome = 'WON' | 'PARTIAL' | 'REJECTED'

type Props = {
  appealId: string
  onSubmitted?: (outcome: Outcome) => void
  variant?: 'inline' | 'card'
}

// Mounted from AccountScreen — users come back days later (via email link)
// to record what the VOA decided. The post-send fulfilment screen no longer
// surfaces this form inline, since outcomes don't arrive for 60+ days.
export const AppealOutcomeForm = ({ appealId, onSubmitted, variant = 'card' }: Props) => {
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  const [refundAmount, setRefundAmount] = useState('')
  const reportOutcome = trpc.appeal.reportOutcome.useMutation({
    onSuccess: (_data, variables) => {
      onSubmitted?.(variables.outcome as Outcome)
    },
  })

  const wrapper = variant === 'card' ? styles.card : styles.inline

  return (
    <View style={wrapper}>
      <Text style={styles.title}>Heard back from the Valuation Office?</Text>
      <Text style={styles.subtitle}>
        Tell us the outcome — we'll stop the reminder cadence and offer the right next step.
      </Text>

      <View style={styles.outcomeRow}>
        {(['WON', 'PARTIAL', 'REJECTED'] as const).map((o) => (
          <Pressable
            accessibilityRole="button"
            key={o}
            onPress={() => setOutcome(o)}
            style={[styles.outcomeButton, outcome === o && styles.outcomeButtonActive]}
          >
            <Text style={[styles.outcomeText, outcome === o && styles.outcomeTextActive]}>
              {o === 'WON' ? 'Won' : o === 'PARTIAL' ? 'Partial' : 'Rejected'}
            </Text>
          </Pressable>
        ))}
      </View>

      {outcome === 'WON' || outcome === 'PARTIAL' ? (
        <>
          <Text style={styles.label}>Refund amount (£, optional)</Text>
          <TextInput
            accessibilityLabel="Refund amount"
            keyboardType="numeric"
            onChangeText={setRefundAmount}
            placeholder="e.g. 1250"
            style={styles.input}
            value={refundAmount}
          />
        </>
      ) : null}

      <Pressable
        accessibilityRole="button"
        disabled={!outcome || reportOutcome.isPending}
        onPress={() =>
          outcome &&
          reportOutcome.mutate({
            appealId,
            outcome,
            refundAmountPence:
              refundAmount.trim().length > 0
                ? Math.round(Number(refundAmount.trim()) * 100)
                : undefined,
          })
        }
        style={[styles.primaryButton, (!outcome || reportOutcome.isPending) && styles.disabled]}
      >
        <Text style={styles.primaryButtonText}>
          {reportOutcome.isPending ? 'Saving…' : 'Submit outcome'}
        </Text>
      </Pressable>
      <ErrorNotice error={reportOutcome.error} />
    </View>
  )
}

const styles = StyleSheet.create({
  card: {
    marginTop: 12,
    padding: 16,
    borderRadius: 10,
    backgroundColor: colors.primary[100],
    borderWidth: 1,
    borderColor: colors.primary[500],
    gap: 10,
  },
  inline: {
    marginTop: 8,
    padding: 12,
    borderRadius: 8,
    backgroundColor: colors.primary[100],
    borderWidth: 1,
    borderColor: colors.primary[500],
    gap: 8,
  },
  title: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.primary[900],
    fontFamily: fontFamily.body,
  },
  subtitle: { fontSize: 12, color: colors.primary[700], fontFamily: fontFamily.body },
  label: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.ink,
    marginTop: 4,
    fontFamily: fontFamily.body,
  },
  input: {
    borderWidth: 1,
    borderColor: colors.borderStrong,
    borderRadius: 8,
    padding: 10,
    fontSize: 15,
    backgroundColor: colors.surface,
    color: colors.ink,
    fontFamily: fontFamily.body,
  },
  outcomeRow: { flexDirection: 'row', gap: 8, marginTop: 4 },
  outcomeButton: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.primary[500],
    backgroundColor: colors.surface,
    alignItems: 'center',
  },
  outcomeButtonActive: { backgroundColor: colors.primary[700], borderColor: colors.primary[700] },
  outcomeText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.primary[700],
    fontFamily: fontFamily.body,
  },
  outcomeTextActive: { color: '#fff' },
  primaryButton: {
    marginTop: 4,
    backgroundColor: colors.primary[700],
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
    fontFamily: fontFamily.body,
  },
  disabled: { opacity: 0.5 },
  error: { color: colors.error[700], fontSize: 12, fontFamily: fontFamily.body },
})
